/**
 * pieceConfirmations — the ONE rule for which line an answer belongs to.
 *
 * F6. The gate keys confirmation tokens by `index`, and the client's answered-token list
 * survives every row edit short of a successful submit: it is cleared only by
 * resetFormAfterSuccess, not by removeItem, addItem, Revert All, a draft load or an
 * Excel/OCR bulk fill.
 *
 * So an ordinal is the wrong key. Type a §14B reason for row 3, go back to the form,
 * delete row 1, submit — and the override was applied to a DIFFERENT item, with that
 * person's typed reason recorded in ItemPieceProfileEvents against the wrong line. Nothing
 * errors; it simply records the wrong fact, which is the worst shape a bug can have in an
 * audit trail.
 *
 * `uid` is the stable row identity (applyItems mints one for every row that lacks it), so
 * the answer carries it and the index is re-derived here, against the array actually being
 * sent, at the moment it is sent.
 *
 * This lives in its own module because the test for it must exercise THIS code. A test
 * that restates the rule beside the implementation is two implementations, and this module
 * has already paid for that mistake more than once.
 */

/**
 * H7.3 — a confirmation token must BE a token before it is sent.
 *
 * The gate matches on `conf.type`. An array, a null, or an object without a type matches
 * nothing, so the blocker re-raises and the person is trapped in a modal with no error to
 * read. Rejecting the shape here makes a malformed token an immediate client-side bug
 * instead of an infinite loop.
 */
export const isValidConfirmationToken = (t) =>
  !!t && typeof t === 'object' && !Array.isArray(t)
  && typeof t.type === 'string' && t.type.length > 0
  && Number.isInteger(Number(t.index));

/**
 * Re-point answered tokens at the rows they were actually about.
 *
 * @param {Array} tokens  answered confirmations, each optionally carrying `rowId`
 * @param {Array} rows    the item array being submitted, each row carrying `uid`
 * @returns {Array} tokens with `index` re-derived; ones whose row is gone are DROPPED
 *
 * A token whose `rowId` no longer exists is dropped, never re-pointed at whatever now
 * occupies that index — a deleted row's override must die with it rather than migrate to
 * its neighbour. A token with no `rowId` predates this and passes through on its index; it
 * can only have come from the same session, and dropping it would throw away an answer the
 * person actually gave.
 */
export const remapConfirmationsToRows = (tokens, rows) => {
  const list = Array.isArray(tokens) ? tokens : [];
  const indexOfUid = new Map((rows || []).map((r, i) => [r && r.uid, i]));
  return list
    .filter(isValidConfirmationToken)
    .map(t => {
      if (t.rowId === undefined || t.rowId === null) return t;
      const idx = indexOfUid.get(t.rowId);
      return idx === undefined ? null : { ...t, index: idx };
    })
    .filter(Boolean);
};

export default remapConfirmationsToRows;
